/**
 * The Recovery Loop drawer's status band — the one loud element on the screen
 * (approved prototype v3). It replaces what used to be said four ways at once:
 * the in-drawer stepper, a separate banner, and the header status pill.
 *
 * Anatomy, top to bottom: a bold phrase saying exactly where the case stands,
 * one truncating line saying why and what happens next, and the journey dots —
 * small and quiet, with a label only under the step that matters. The hue says
 * whose move it is (amber = the patient's, red = staff's, green = recovered,
 * gray = nobody's), so a receptionist can triage the queue by color alone.
 *
 * The words come from utils/recoveryBand (pure + unit-tested); the journey
 * comes from utils/recoveryStepper, so the dots here and the full track on the
 * case row can never tell different stories.
 */
import { buildRecoverySteps, type RecoveryStep } from '../utils/recoveryStepper'
import type { RecoveryBand } from '../utils/recoveryBand'
import type { RecoveryDisplayState } from '../services/recoveryCases'

/**
 * How a step's tone renders as a dot: the step that matters is filled and
 * labelled, finished ones are plain, unreached ones are washed out.
 *
 * A muted step needs its icon to decide: a muted CHECK is travelled road, but a
 * muted X is where the journey stopped (the patient opted out) — that one is
 * the step worth naming.
 */
function dotClass(step: RecoveryStep): 'on' | 'done' | 'off' {
  switch (step.tone) {
    case 'current':
    case 'error':
      return 'on'
    case 'done':
      return 'done'
    case 'muted':
      return step.icon === 'x' ? 'on' : 'done'
    case 'pending':
      return 'off'
  }
}

/** Whether a step is the one the band should name. */
function isFocus(step: RecoveryStep): boolean {
  return dotClass(step) === 'on'
}

interface RecoveryStatusBandProps {
  band: RecoveryBand
  state: RecoveryDisplayState
}

export default function RecoveryStatusBand({ band, state }: RecoveryStatusBandProps) {
  const steps = buildRecoverySteps(state)
  // A journey that ran to completion has no step to single out, so the last one
  // carries the label — otherwise a recovered case shows dots with no words.
  // Only a genuinely finished track gets that treatment: an opted-out case ends
  // on its own muted X, and labelling ITS last step would read "Resolved" for a
  // patient who told us to stop.
  const hasFocus = steps.some(isFocus)

  return (
    <div className={`rl-band rl-band--${band.hue}`} role="status">
      <b className="rl-band__headline">{band.headline}</b>
      {band.subline && <span className="rl-band__sub">{band.subline}</span>}
      <div className="rl-band__strip" aria-label="Recovery progress">
        {steps.map((step, i) => {
          const cls = dotClass(step)
          const labelled = cls === 'on' || (!hasFocus && i === steps.length - 1)
          return (
            <span className="rl-band__step" key={`${step.label}-${i}`}>
              <span className={`rl-band__dot rl-band__dot--${cls}`} />
              {labelled && <span className="rl-band__step-label">{step.label}</span>}
            </span>
          )
        })}
      </div>
    </div>
  )
}
